import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR, NOOP } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IEmployeeAppInboxResponse,
  IExperienceDenormalizedV2
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { css } from '@emotion/css';
import { Theme, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  ButtonBase,
  InputAdornment,
  Tooltip,
  Typography
} from '@material-ui/core';
import { assertNever } from 'assert-never';
import { compact, partition } from 'lodash';
import { CheckCircle, Mail, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ChipFilterWithDropdown } from '../../../../../../../components/ChipFilterWithDropdown';
import { Loader } from '../../../../../../../components/Loader';
import { TableSearch } from '../../../../../../../components/Table';
import { StandardDrawer } from '../../../../../../../features/Experiences/components/StandardDrawer';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import {
  UnsafeTFunction,
  useTranslation,
  useUnsafeTranslation
} from '../../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../../layout/Flex';
import { LightMdToJsx } from '../../../../../../../services/formatter';
import { useProductAnalytics } from '../../../../../../features/Experience/services/productAnalytics';
import { useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate } from '../../../services/retrieval';
import {
  getIndividualMessageUrl,
  getIndividualTaskUrl
} from '../../../services/routes';
import { MatchedItem, searchInInstance } from '../../../services/search';
import { RoundIconContainer } from '../../RoundIconContainer';
import { TopNavBar } from '../../TopNav';
import {
  InboxSearchResultsList,
  useInboxSearchResults,
  useInboxSearchSelectMatchTrackFn
} from '../WithinInbox';
import {
  inboxDataToMatchCandidates,
  inboxSearchItemToHref
} from '../WithinInbox/service';

export const getUrlForItem = (
  item: MatchedItem,
  actorAccessToken: string,
  instanceAccessToken: string,
  returnTo?: string
) => {
  if (item.type === 'MSG') {
    return getIndividualMessageUrl(
      actorAccessToken,
      instanceAccessToken,
      item.item.templateId,
      returnTo
    );
  }
  if (item.type === 'TASK') {
    return getIndividualTaskUrl(
      actorAccessToken,
      instanceAccessToken,
      item.item.id,
      returnTo
    );
  }
  return null;
};

const ResultsContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2)
}));

const ResultsHeading = styled('div')((p) => ({
  marginBottom: p.theme.spacing()
}));

const ItemContainer = styled('div')((p) => ({
  padding: `${p.theme.spacing(1.5)}px ${p.theme.spacing(1)}px`,
  cursor: 'pointer',
  ':hover': {
    background: p.theme.custom.colors.gray.w100
  },
  borderBottom: `${p.theme.custom.border.standard}`
}));

const ShowMoreButtonContainer = styled('div')((p) => ({
  color: p.theme.custom.colors.blue.w400
}));

const ItemIcon = ({ d }: { d: MatchedItem }) => {
  const iconSize = 16;
  if (d.type === 'MSG') {
    return <Mail size={iconSize} />;
  }

  if (d.type === 'TASK') {
    return <CheckCircle size={iconSize} />;
  }

  return null;
};

const getItemStart = (d: MatchedItem, t: UnsafeTFunction): string => {
  // TODO Triggered Items - tja, what do we write here?
  if (d.type === 'TASK') {
    return t('Day {{ day }}', {
      day: 'TODO'
    });
  }
  if (d.type === 'MSG') {
    return t('Day {{ day }}', {
      day: 'TODO'
    });
  }

  return '';
};

const Item = ({
  d,
  onSelect,
  onShowMoreResults,
  iconProps
}: {
  d: MatchedItem;
  onSelect: () => void;
  onShowMoreResults?: () => void;
  iconProps: {
    color: string;
    tooltip: string;
  };
}) => {
  const { t } = useUnsafeTranslation();
  const [showAllResults, setShowAllResults] = useState(false);
  const matches = d.matches.filter((m) => m.blockId !== 'TITLE');
  const matchesToShow = showAllResults ? matches : matches.slice(0, 1);
  const itemStart = getItemStart(d, t);
  const titleMatch = d.matches.find((x) => x.blockId === 'TITLE');
  // We use this as context matches come formatted with bolding of matches etc
  const itemTitle = titleMatch
    ? titleMatch.match.withContext.txt
    : d.type === 'MSG'
    ? d.item.subject
    : d.item.title;
  return (
    <ItemContainer role="button" onClick={onSelect}>
      <FlexContainer justifyContent="flex-start" alignItems="flex-start">
        <div>
          <Tooltip title={iconProps.tooltip}>
            <RoundIconContainer size="small">
              <ItemIcon d={d} />
            </RoundIconContainer>
          </Tooltip>
        </div>
        <FlexContainer spacing={1} direction="column" alignItems="start">
          <FlexContainer spacing={0.5} direction="column" alignItems="start">
            <LightMdToJsx>{itemTitle}</LightMdToJsx>
            {/* <Typography variant="caption">
              <FlexContainer spacing={0.5}>
                {itemStart && <div>{itemStart}</div>}
              </FlexContainer>
            </Typography> */}
          </FlexContainer>
          {!!matches.length && (
            <FlexContainer spacing={0.5} direction="column" alignItems="start">
              {matchesToShow.map((m, i) => (
                <LightMdToJsx key={i}>{m.match.withContext.txt}</LightMdToJsx>
              ))}
              {!showAllResults && matches.length > 1 && (
                <ShowMoreButtonContainer>
                  <ButtonBase
                    onClick={(ev) => {
                      ev.stopPropagation();
                      ev.preventDefault();
                      onShowMoreResults?.();
                      setShowAllResults(true);
                    }}
                  >
                    {t('+ {{ count }} more {{ result }}', {
                      count: matches.length - 1,
                      result:
                        matches.length - 1 === 1 ? t('result') : t('results')
                    })}
                  </ButtonBase>
                </ShowMoreButtonContainer>
              )}
            </FlexContainer>
          )}
        </FlexContainer>
      </FlexContainer>
    </ItemContainer>
  );
};

const getIconProps = (
  d: MatchedItem,
  theme: Theme,
  t: UnsafeTFunction
): {
  color: string;
  tooltip: string;
} => {
  switch (d.type) {
    case 'MSG':
      return {
        // Normal colors here
        color: 'blue', // msgActor?.color || '',
        tooltip: `${t('Message')}`
      };
    case 'TASK':
      return {
        color: 'blue', // taskActor?.color || '',
        tooltip: `${t('Task')}`
      };
    default:
      return assertNever(d);
  }
};

const DrawerInputContainer = styled(TopNavBar)((p) => ({
  paddingLeft: p.theme.spacing(2),
  paddingRight: p.theme.spacing(2)
}));

const DrawerBodyContainer = styled('div')((p) => ({
  padding: p.theme.spacing(2),
  paddingTop: p.theme.spacing(1)
}));

const SEARCH_ICON_SIZE = 20;

const SearchResultsFromInboxInner = ({
  data,
  q,
  currentInstanceAccessToken,
  types,
  minimumSearchLengthReached,
  onSearch
}: {
  data: IEmployeeAppInboxResponse;
  q: string;
  currentInstanceAccessToken: string;
  types: ('USER' | 'MSG' | 'TASK')[];
  minimumSearchLengthReached: boolean;
  onSearch: (q: string, noOfResults: number) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const onSelectMatch = useInboxSearchSelectMatchTrackFn(data.userAccessToken);

  const candidates = useMemo(() => {
    const filteredData: IEmployeeAppInboxResponse = {
      ...data,
      tasks: data.tasks.filter(
        (t) => t.instanceAccessToken !== currentInstanceAccessToken
      ),
      messages: data.messages.filter(
        (m) => m.instanceAccessToken !== currentInstanceAccessToken
      )
    };
    return inboxDataToMatchCandidates(filteredData);
  }, [data, currentInstanceAccessToken]);

  const { results, allResultsCount } = useInboxSearchResults({
    q,
    minimumSearchLengthReached,
    candidates,
    types,
    onSearch
  });

  if (!candidates.length || !minimumSearchLengthReached || !results.length) {
    return null;
  }

  return (
    <ResultsContainer>
      <ResultsHeading>
        <Typography variant="h6">
          {t('Results from other active experiences')}
        </Typography>
      </ResultsHeading>
      <InboxSearchResultsList
        q={q}
        results={results}
        allResultsCount={allResultsCount}
        onSelectMatch={onSelectMatch}
        onMoreResultsClicked={NOOP}
        itemToHref={(d) => inboxSearchItemToHref(data.userAccessToken, d)}
        theme={theme}
        t={t}
      />
      {!!results.length && (
        <FlexContainerVertical
          alignItems="center"
          justifyContent="center"
          marginTop={2}
          spacing={0.5}
        >
          <Typography variant="caption">
            {t(`Archived experiences won't appear in search results.`)}
          </Typography>
          <Typography variant="caption">
            {t(`You can find these on your Dashboard.`)}
          </Typography>
        </FlexContainerVertical>
      )}
    </ResultsContainer>
  );
};

const SearchResultsFromInbox = ({
  q,
  userAccessToken,
  currentInstanceAccessToken,
  types,
  minimumSearchLengthReached,
  onSearch
}: {
  q: string;
  userAccessToken: string;
  currentInstanceAccessToken: string;
  types: ('USER' | 'MSG' | 'TASK')[];
  minimumSearchLengthReached: boolean;
  onSearch: (q: string, noOfResults: number) => void;
}) => {
  const [data, loading] = useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate(
    userAccessToken
  );
  // Don't even show a loader when user hasn't made a request yet, but we're still
  // loading the data in the background
  if (!minimumSearchLengthReached) {
    return null;
  }
  if (loading) {
    return (
      <ResultsContainer>
        <ResultsHeading>
          <FlexContainer justifyContent="center" alignItems="center">
            <Loader size={12} />
          </FlexContainer>
        </ResultsHeading>
      </ResultsContainer>
    );
  }
  if (!data) {
    return null;
  }

  return (
    <SearchResultsFromInboxInner
      data={data.inbox}
      q={q}
      currentInstanceAccessToken={currentInstanceAccessToken}
      types={types}
      minimumSearchLengthReached={minimumSearchLengthReached}
      onSearch={onSearch}
    />
  );
};

const MINIMUM_SEARCH_LENGTH = 3;
export const SearchWithinInstanceDrawer = ({
  instance,
  open,
  onClose,
  onSelectMatch,
  onSearch = NOOP,
  onMoreResultsClicked = NOOP,
  onFilter,
  allowGlobalSearch = false
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  open: boolean;
  onClose: () => void;
  onSelectMatch: (
    match: MatchedItem,
    i: number,
    q: string,
    noOfResults: number
  ) => void;
  onSearch?: (q: string, noOfResults: number) => void;
  onMoreResultsClicked?: (match: MatchedItem, i: number) => void;
  onFilter?: (
    type: 'ITEM_TYPE',
    selected: {
      key: string;
      label: string;
    }[],
    deselected: { key: string; label: string }[]
  ) => void;
  allowGlobalSearch?: boolean;
}) => {
  const { t } = useUnsafeTranslation();

  const [q, setQ] = useState(''); // better to store in url - but introduces race conditions again...
  const [showUpcoming, setShowUpcoming] = useState(false);

  const minimumSearchLengthReached = q.length >= MINIMUM_SEARCH_LENGTH;

  const theme = useTheme();

  const typeOptions: {
    key: 'USER' | 'MSG' | 'TASK';
    label: string;
  }[] = useMemo(
    () =>
      compact([
        allowGlobalSearch ? { key: 'USER', label: t('Person') } : null,
        { key: 'MSG', label: t('Message') },
        { key: 'TASK', label: t('Task') }
      ]),
    [t, allowGlobalSearch]
  );

  const [selectedType, setSelectedType] = useState(typeOptions);

  const { results, hasUpcoming, allResultsCount } = useMemo(() => {
    if (!minimumSearchLengthReached) {
      return { results: EMPTY_ARR, hasUpcoming: false, allResultsCount: 0 };
    }
    const nextResults = searchInInstance(instance, q, {
      contextLength: 54,
      filterByType: selectedType.map((a) => a.key)
    });
    onSearch(q, nextResults.length);
    if (showUpcoming) {
      return {
        results: nextResults,
        hasUpcoming: false,
        allResultsCount: nextResults.length
      };
    } else {
      const n = Date.now();
      const [upcoming, others] = partition(nextResults, (x) => {
        const date = x.type === 'MSG' ? x.item.sendAt : x.item.start;
        return date.toMillis() > n;
      });
      return {
        results: others,
        hasUpcoming: !!upcoming.length,
        allResultsCount: nextResults.length
      };
    }
  }, [
    instance,
    q,
    minimumSearchLengthReached,
    onSearch,
    selectedType,
    showUpcoming
  ]);

  return (
    <StandardDrawer open={open} onClose={onClose} noGutter>
      <DrawerInputContainer>
        <TableSearch
          term={q}
          onChange={setQ}
          autoFocus={true}
          fullWidth
          placeholder=""
          className={css({
            borderRadius: 20
          })}
          InputProps={{
            className: css({
              '&&': {
                borderRadius: 20
              }
            }),
            startAdornment: (
              <InputAdornment position="start">
                <Search
                  size={SEARCH_ICON_SIZE}
                  color={theme.palette.primary.main}
                />
              </InputAdornment>
            )
          }}
        />
      </DrawerInputContainer>
      <DrawerBodyContainer>
        <FlexContainer direction="column" alignItems="flex-start">
          <FlexContainer>
            <ChipFilterWithDropdown
              label={t('Filter by item type')}
              options={typeOptions}
              selectedOptions={selectedType}
              onChange={(nextSelected) => {
                if (onFilter) {
                  const [selected, deselected] = partition(typeOptions, (o) =>
                    nextSelected.find((x) => x.key === o.key)
                  );
                  onFilter('ITEM_TYPE', selected, deselected);
                }
                setSelectedType(nextSelected);
              }}
            />
          </FlexContainer>
        </FlexContainer>
        <ResultsContainer>
          {allowGlobalSearch && minimumSearchLengthReached && (
            <Typography variant="h6">
              {t('Results from this experience')}
            </Typography>
          )}
          <ResultsHeading>
            {!results.length && (
              <FlexContainer
                justifyContent="center"
                marginTop={4}
                marginBottom={4}
              >
                <Typography variant="caption">
                  <i>
                    {minimumSearchLengthReached
                      ? t('No matches found')
                      : t('Please type at least {{ minCount }} characters', {
                          minCount: MINIMUM_SEARCH_LENGTH
                        })}
                  </i>
                </Typography>
              </FlexContainer>
            )}
            {results.map((d, i) => (
              <Item
                key={i}
                d={d}
                onSelect={() => onSelectMatch(d, i, q, allResultsCount)}
                onShowMoreResults={() => onMoreResultsClicked(d, i)}
                iconProps={getIconProps(d, theme, t)}
              />
            ))}
            {hasUpcoming && !showUpcoming && (
              <FlexContainer
                fullWidth
                onClick={() => setShowUpcoming(true)}
                role="button"
                justifyContent="center"
                style={{
                  cursor: 'pointer'
                }}
              >
                <Typography variant="caption">
                  {t('See results from upcoming tasks and messages')}
                </Typography>
              </FlexContainer>
            )}
          </ResultsHeading>
        </ResultsContainer>

        {allowGlobalSearch && instance.data.userAccessToken && (
          <SearchResultsFromInbox
            q={q}
            userAccessToken={instance.data.userAccessToken}
            currentInstanceAccessToken={instance.id}
            types={selectedType.map((a) => a.key)}
            minimumSearchLengthReached={minimumSearchLengthReached}
            onSearch={onSearch}
          />
        )}
      </DrawerBodyContainer>
    </StandardDrawer>
  );
};

export const SearchWithinInstanceButton = ({
  instance,
  onSelectMatch,
  closeOnSelect,
  allowGlobalSearch = false
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  onSelectMatch: (match: MatchedItem) => void;
  closeOnSelect?: boolean;
  allowGlobalSearch?: boolean;
}) => {
  const { t } = useTranslation();
  const { trackEvent } = useProductAnalytics(instance.data.accessToken);
  const theme = useTheme();
  const {
    dialogOpen: drawerOpen,
    openDialog: openDrawer,
    closeDialog: closeDrawer
  } = useDialogState();

  const trackingFns = useMemo(
    () => ({
      onSearch: (q: string, noOfResults: number) => {
        trackEvent({
          eventType: PAEventType.EMPLOYEE_EXPERIENCE_INSTANCE,
          name: 'Search Query Submitted',
          Explanation: 'User searched the employee app',
          Query: q,
          'Number Of Results': noOfResults
        });
      },

      onSelect: (
        match: MatchedItem,
        i: number,
        q: string,
        noOfResults: number
      ) => {
        trackEvent({
          eventType: PAEventType.EMPLOYEE_EXPERIENCE_INSTANCE,
          name: 'Search Result Clicked',
          Explanation: 'User clicked on a search result',
          Query: q,
          'Number Of Results': noOfResults,
          'Result Type': match.type === 'MSG' ? 'Message' : 'Task',
          'Result ID': match.item.templateId
        });
      }
    }),
    [trackEvent]
  );

  return (
    <>
      <ButtonBase
        className={css({
          '&&': {
            background: theme.custom.colors.gray.w100,
            color: theme.palette.primary.main,
            padding: `${theme.spacing()}px ${theme.spacing(2)}px`,
            borderRadius: 20,
            minWidth: 208,
            justifyContent: 'flex-start'
          }
        })}
        onClick={() => {
          // trackingFns.onOpen();
          openDrawer();
        }}
      >
        <Search size={SEARCH_ICON_SIZE} />
      </ButtonBase>
      <SearchWithinInstanceDrawer
        instance={instance}
        allowGlobalSearch={allowGlobalSearch}
        open={drawerOpen}
        onClose={closeDrawer}
        onSelectMatch={(match, i, q, allResultsCount) => {
          trackingFns.onSelect(match, i, q, allResultsCount);
          onSelectMatch(match);
          if (closeOnSelect) {
            closeDrawer();
          }
        }}
        onSearch={trackingFns.onSearch}
      />
    </>
  );
};
